// ============================================
// Opportunity Detail Page
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DetailPageSkeleton } from '@/components/skeletons';
import {
  OPPORTUNITY_STAGE,
  type OpportunityStage,
  type UpdateOpportunity,
  type CreateLineItem,
  type OpportunityLineItem,
} from '@muraka/shared';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Building2,
  Calendar,
  TrendingUp,
  Plus,
  AlertCircle,
  Loader2,
  MoreVertical,
  Check,
  X,
} from 'lucide-react';

// Stage configuration with labels, colors, and probabilities
const stageConfig: Record<
  OpportunityStage,
  { label: string; colorClass: string; iconBgClass: string; probability: number }
> = {
  qualification: {
    label: 'Qualification',
    colorClass: 'bg-slate-100 text-slate-700',
    iconBgClass: 'bg-slate-100 text-slate-600',
    probability: 10,
  },
  needs_analysis: {
    label: 'Needs Analysis',
    colorClass: 'bg-blue-100 text-blue-700',
    iconBgClass: 'bg-blue-100 text-blue-600',
    probability: 25,
  },
  proposal: {
    label: 'Proposal',
    colorClass: 'bg-purple-100 text-purple-700',
    iconBgClass: 'bg-purple-100 text-purple-600',
    probability: 50,
  },
  negotiation: {
    label: 'Negotiation',
    colorClass: 'bg-orange-100 text-orange-700',
    iconBgClass: 'bg-orange-100 text-orange-600',
    probability: 75,
  },
  closed_won: {
    label: 'Closed Won',
    colorClass: 'bg-green-100 text-green-700',
    iconBgClass: 'bg-green-100 text-green-600',
    probability: 100,
  },
  closed_lost: {
    label: 'Closed Lost',
    colorClass: 'bg-red-100 text-red-700',
    iconBgClass: 'bg-red-100 text-red-600',
    probability: 0,
  },
};

// Open stages for progress bar
const openStages: OpportunityStage[] = [
  OPPORTUNITY_STAGE.QUALIFICATION,
  OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
  OPPORTUNITY_STAGE.PROPOSAL,
  OPPORTUNITY_STAGE.NEGOTIATION,
];

// Avatar/icon colors
const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
];

const iconColors = [
  'bg-blue-100 text-blue-600',
  'bg-green-100 text-green-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-red-100 text-red-600',
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const getIconColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return iconColors[Math.abs(hash) % iconColors.length];
};

const getInitials = (name: string) => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date: string) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatFullDate = (date: string) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function OpportunityDetailPage() {
  const { id } = useParams({ from: '/opportunities/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState('details');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lineItemModalOpen, setLineItemModalOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contactId: '',
    amount: 0,
    expectedCloseDate: '',
    notes: '',
    stage: OPPORTUNITY_STAGE.QUALIFICATION as OpportunityStage,
  });

  // Line item form
  const [lineItemData, setLineItemData] = useState({
    productName: '',
    quantity: 1,
    unitPrice: 0,
    notes: '',
  });

  // Query opportunity
  const {
    data: opportunity,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => api.getOpportunity(id),
    enabled: !!id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateOpportunity) => api.updateOpportunity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      setEditModalOpen(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteOpportunity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      navigate({ to: '/opportunities' });
    },
  });

  // Update stage mutation
  const updateStageMutation = useMutation({
    mutationFn: (stage: OpportunityStage) =>
      api.updateOpportunityStage(
        id,
        stage,
        stage === OPPORTUNITY_STAGE.CLOSED_LOST ? 'Lost' : undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });

  // Add line item mutation
  const addLineItemMutation = useMutation({
    mutationFn: (data: CreateLineItem) => api.addLineItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
      setLineItemModalOpen(false);
      setLineItemData({ productName: '', quantity: 1, unitPrice: 0, notes: '' });
    },
  });

  // Delete line item mutation
  const deleteLineItemMutation = useMutation({
    mutationFn: (lineItemId: string) => api.deleteLineItem(id, lineItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
    },
  });

  // Open edit modal with current data
  const openEditModal = () => {
    if (opportunity) {
      setFormData({
        name: opportunity.name,
        contactId: opportunity.contactId || '',
        amount: opportunity.amount,
        expectedCloseDate: opportunity.expectedCloseDate.split('T')[0],
        notes: opportunity.notes || '',
        stage: opportunity.stage as OpportunityStage,
      });
      setEditModalOpen(true);
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: UpdateOpportunity = {
      name: formData.name,
      contactId: formData.contactId || null,
      amount: formData.amount,
      expectedCloseDate: formData.expectedCloseDate,
      notes: formData.notes || null,
    };
    updateMutation.mutate(updateData);
  };

  const handleAddLineItem = (e: React.FormEvent) => {
    e.preventDefault();
    addLineItemMutation.mutate({
      productName: lineItemData.productName,
      quantity: lineItemData.quantity,
      unitPrice: lineItemData.unitPrice,
      notes: lineItemData.notes || null,
    });
  };

  const handleMarkAsWon = () => {
    updateStageMutation.mutate(OPPORTUNITY_STAGE.CLOSED_WON);
    setMoreMenuOpen(false);
  };

  const handleMarkAsLost = () => {
    updateStageMutation.mutate(OPPORTUNITY_STAGE.CLOSED_LOST);
    setMoreMenuOpen(false);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
          <DetailPageSkeleton />
        </div>
      </AppLayout>
    );
  }

  if (error || !opportunity) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <p className="text-destructive mb-2">Failed to load opportunity</p>
          <Button variant="outline" onClick={() => navigate({ to: '/opportunities' })}>
            Back to Opportunities
          </Button>
        </div>
      </AppLayout>
    );
  }

  const isClosed =
    opportunity.stage === OPPORTUNITY_STAGE.CLOSED_WON ||
    opportunity.stage === OPPORTUNITY_STAGE.CLOSED_LOST;
  const currentStageIndex = openStages.indexOf(opportunity.stage as OpportunityStage);
  const lineItems: OpportunityLineItem[] = opportunity.lineItems || [];
  const lineItemsTotal = lineItems.reduce(
    (sum: number, item: OpportunityLineItem) => sum + item.totalPrice,
    0
  );

  // Get stage progress class
  const getStageProgressClass = (stage: OpportunityStage) => {
    const stageIndex = openStages.indexOf(stage);
    if (opportunity.stage === 'closed_won') return 'bg-green-500';
    if (opportunity.stage === 'closed_lost') return 'bg-red-500';
    if (stageIndex <= currentStageIndex) return 'bg-primary';
    return 'bg-border';
  };

  return (
    <AppLayout>
      {/* Opportunity Header Section */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 -mt-6 mb-0">
        {/* Back Button Bar */}
        <div className="h-14 border-b border-border flex items-center px-4 lg:px-6">
          <button
            onClick={() => navigate({ to: '/opportunities' })}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Opportunities</span>
          </button>
        </div>

        {/* Opportunity Info Header */}
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Opportunity Info */}
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-lg flex items-center justify-center shrink-0 ${stageConfig[opportunity.stage as OpportunityStage]?.iconBgClass}`}
              >
                <TrendingUp className="h-8 w-8" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-foreground">{opportunity.name}</h1>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${stageConfig[opportunity.stage as OpportunityStage]?.colorClass}`}
                  >
                    {stageConfig[opportunity.stage as OpportunityStage]?.label}
                  </span>
                </div>
                <p className="text-3xl font-bold text-foreground mt-2">
                  {formatCurrency(opportunity.amount)}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    Close: {formatDate(opportunity.expectedCloseDate)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    {opportunity.probability}% probability
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <button
                onClick={handleMarkAsWon}
                disabled={isClosed || updateStageMutation.isPending}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isClosed
                    ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground'
                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                }`}
              >
                <Check className="h-4 w-4" />
                Mark Won
              </button>
              <div className="relative">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
                >
                  <MoreVertical className="h-5 w-5" />
                </button>
                {moreMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-10">
                    <button
                      onClick={handleMarkAsLost}
                      disabled={isClosed}
                      className="w-full px-4 py-2 text-left text-sm text-muted-foreground hover:bg-muted flex items-center gap-2 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                      Mark Lost
                    </button>
                    <div className="border-t border-border my-1"></div>
                    <button
                      onClick={() => {
                        setDeleteModalOpen(true);
                        setMoreMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Opportunity
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Progress Bar */}
      {!isClosed && (
        <div className="bg-card border-b border-border -mx-4 lg:-mx-6">
          <div className="max-w-5xl mx-auto px-4 lg:px-6 py-4">
            <div className="flex items-center gap-1">
              {openStages.map((stage, index) => (
                <div key={stage} className="flex-1 flex items-center">
                  <div
                    className={`flex-1 h-2 rounded-full transition-colors ${getStageProgressClass(stage)}`}
                  ></div>
                  {index < openStages.length - 1 && (
                    <div
                      className={`w-2 h-2 rounded-full mx-1 ${
                        index < currentStageIndex ? 'bg-primary' : 'bg-border'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2">
              {openStages.map(stage => (
                <span
                  key={stage}
                  className={`text-xs text-center ${
                    opportunity.stage === stage
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground'
                  }`}
                >
                  {stageConfig[stage].label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="bg-card border-b border-border -mx-4 lg:-mx-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 lg:px-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'products'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Products ({lineItems.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto py-6">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content (2 columns) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Opportunity Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Opportunity Information</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  <dl className="grid gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <dt className="text-sm text-muted-foreground">Opportunity Name</dt>
                      <dd className="mt-1 text-foreground">{opportunity.name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Amount</dt>
                      <dd className="mt-1 text-xl font-semibold text-foreground">
                        {formatCurrency(opportunity.amount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Stage</dt>
                      <dd className="mt-2">
                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${stageConfig[opportunity.stage as OpportunityStage]?.colorClass}`}
                        >
                          {stageConfig[opportunity.stage as OpportunityStage]?.label}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Close Date</dt>
                      <dd className="mt-1 text-foreground">
                        {formatDate(opportunity.expectedCloseDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Probability</dt>
                      <dd className="mt-1 text-foreground">{opportunity.probability}%</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Notes Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Notes</h2>
                  <button onClick={openEditModal} className="text-sm text-primary hover:underline">
                    Edit
                  </button>
                </div>
                <div className="p-6">
                  {opportunity.notes ? (
                    <p className="text-foreground whitespace-pre-wrap">{opportunity.notes}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No notes added yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar (1 column) */}
            <div className="space-y-6">
              {/* Related Account Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Account</h2>
                </div>
                <div className="p-6">
                  {opportunity.accountName ? (
                    <button
                      onClick={() =>
                        navigate({ to: '/accounts/$id', params: { id: opportunity.accountId } })
                      }
                      className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors w-full text-left"
                    >
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${getIconColor(opportunity.accountName)}`}
                      >
                        <Building2 className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{opportunity.accountName}</p>
                        <p className="text-sm text-muted-foreground">View account</p>
                      </div>
                    </button>
                  ) : (
                    <p className="text-muted-foreground italic">No account associated.</p>
                  )}
                </div>
              </div>

              {/* Related Contact Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">Primary Contact</h2>
                </div>
                <div className="p-6">
                  {opportunity.contactId && opportunity.contactName ? (
                    <button
                      onClick={() =>
                        navigate({ to: '/contacts/$id', params: { id: opportunity.contactId! } })
                      }
                      className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors w-full text-left"
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${getAvatarColor(opportunity.contactName)}`}
                      >
                        <span className="text-sm font-medium">
                          {getInitials(opportunity.contactName)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{opportunity.contactName}</p>
                        <p className="text-sm text-muted-foreground">View contact</p>
                      </div>
                    </button>
                  ) : (
                    <p className="text-muted-foreground italic">No contact associated.</p>
                  )}
                </div>
              </div>

              {/* System Information Card */}
              <div className="bg-card rounded-xl border border-border">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">System Information</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Created</dt>
                    <dd className="mt-1 text-foreground">
                      {formatFullDate(opportunity.createdAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Last Modified</dt>
                    <dd className="mt-1 text-foreground">
                      {formatFullDate(opportunity.updatedAt)}
                    </dd>
                  </div>
                  {opportunity.actualCloseDate && (
                    <div>
                      <dt className="text-sm text-muted-foreground">Closed Date</dt>
                      <dd className="mt-1 text-foreground">
                        {formatFullDate(opportunity.actualCloseDate)}
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-card rounded-xl border border-border">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">Line Items ({lineItems.length})</h2>
              <Button size="sm" onClick={() => setLineItemModalOpen(true)} disabled={isClosed}>
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>
            {lineItems.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <p>No line items yet. Add products or services to this opportunity.</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item: OpportunityLineItem) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unitPrice)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.totalPrice)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteLineItemMutation.mutate(item.id)}
                            disabled={isClosed || deleteLineItemMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end px-6 py-4 border-t bg-muted/50">
                  <div className="text-right">
                    <span className="text-muted-foreground mr-4">Line Items Total:</span>
                    <span className="font-bold text-lg">{formatCurrency(lineItemsTotal)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <DialogContent onClose={() => setEditModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Opportunity</DialogTitle>
            <DialogDescription>Update opportunity information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="p-6 pt-0 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <Input
                label="Opportunity Name"
                required
                placeholder="Acme Corp - Enterprise License"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Amount ($)"
                  type="number"
                  min="0"
                  placeholder="50000"
                  value={formData.amount || ''}
                  onChange={e => setFormData({ ...formData, amount: Number(e.target.value) })}
                />
                <Input
                  label="Expected Close Date"
                  type="date"
                  required
                  value={formData.expectedCloseDate}
                  onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                />
              </div>
              <Textarea
                label="Notes"
                rows={3}
                placeholder="Add any relevant notes..."
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
            <DialogFooter className="bg-muted/30">
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Line Item Modal */}
      <Dialog open={lineItemModalOpen} onClose={() => setLineItemModalOpen(false)}>
        <DialogContent onClose={() => setLineItemModalOpen(false)} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>Add a product or service to this opportunity.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddLineItem}>
            <div className="space-y-4 p-6 pt-0">
              <Input
                label="Product Name"
                required
                placeholder="Product or service name"
                value={lineItemData.productName}
                onChange={e => setLineItemData({ ...lineItemData, productName: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Quantity"
                  type="number"
                  min="1"
                  required
                  value={lineItemData.quantity}
                  onChange={e =>
                    setLineItemData({ ...lineItemData, quantity: Number(e.target.value) })
                  }
                />
                <Input
                  label="Unit Price ($)"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={lineItemData.unitPrice || ''}
                  onChange={e =>
                    setLineItemData({ ...lineItemData, unitPrice: Number(e.target.value) })
                  }
                />
              </div>
              <Textarea
                label="Notes"
                rows={2}
                placeholder="Optional notes"
                value={lineItemData.notes}
                onChange={e => setLineItemData({ ...lineItemData, notes: e.target.value })}
              />
              {lineItemData.productName && lineItemData.unitPrice > 0 && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Total:{' '}
                    <span className="font-medium text-foreground">
                      {formatCurrency(lineItemData.quantity * lineItemData.unitPrice)}
                    </span>
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLineItemModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addLineItemMutation.isPending}>
                {addLineItemMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Add Item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogContent onClose={() => setDeleteModalOpen(false)} className="max-w-md">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Opportunity?</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">{opportunity.name}</span>? All
              associated data will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete Opportunity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
